import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Button,
  Typography,
  Box,
  IconButton,
  useTheme
} from '@mui/material'
import BackspaceIcon from '@mui/icons-material/Backspace'
import CloseIcon from '@mui/icons-material/Close'
import PropTypes from 'prop-types'

/**
 * Numeric Keypad component.
 *
 * @param {Object} props - Component properties.
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {Function} props.onClose - Function to close the dialog.
 * @param {Function} props.onAccept - Function called when accepting the value. Receives the numeric value.
 * @param {string|number} props.initialValue - Initial value to display.
 * @param {string} props.title - Dialog title text.
 * @param {boolean} props.allowDecimals - Whether decimal values are allowed.
 */
const NumericKeypad = ({ open, onClose, onAccept, initialValue = 0, title = 'Report Quantity', allowDecimals = false }) => {
  const theme = useTheme()
  const [value, setValue] = useState(initialValue.toString())

  useEffect(() => {
    if (open) {
      setValue(initialValue.toString())
    }
  }, [open, initialValue])

  const handleNumberClick = useCallback((num) => {
    setValue((prev) => (prev === '0' ? num.toString() : prev + num))
  }, [])

  const handleBackspace = useCallback(() => {
    setValue((prev) => (prev.length > 1 ? prev.slice(0, -1) : '0'))
  }, [])

  const handleClear = useCallback(() => {
    setValue('0')
  }, [])

  const handleDot = useCallback(() => {
    if (allowDecimals && !value.includes('.')) {
      setValue((prev) => prev + '.')
    }
  }, [allowDecimals, value])

  const handleDoubleZero = useCallback(() => {
    setValue((prev) => (prev === '0' ? '0' : prev + '00'))
  }, [])

  const handleAccept = useCallback(() => {
    let numValue = parseFloat(value)
    if (!allowDecimals) {
      numValue = Math.floor(numValue)
    }
    onAccept(isNaN(numValue) ? 0 : numValue)
    onClose()
  }, [value, allowDecimals, onAccept, onClose])

  useEffect(() => {
    if (!open) {
      return
    }

    const handleKeyDown = (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey) {
        return
      }

      const { key } = event

      if (key >= '0' && key <= '9') {
        handleNumberClick(Number(key))
      } else if (key === '.' || key === ',') {
        handleDot()
      } else if (key === 'Backspace') {
        handleBackspace()
      } else if (key === 'Delete') {
        handleClear()
      } else if (key === 'Enter') {
        handleAccept()
      } else if (key === 'Escape') {
        onClose()
      } else {
        return
      }

      event.preventDefault()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, handleNumberClick, handleBackspace, handleClear, handleDot, handleAccept, onClose])

  const KeyButton = ({ children, onClick, color = 'inherit', sx = {}, variant = 'contained', disabled = false }) => (
    <Button
      fullWidth
      variant={variant}
      onClick={onClick}
      color={color}
      disabled={disabled}
      sx={{
        height: 64,
        fontSize: '1.5rem',
        borderRadius: 1,
        textTransform: 'none',
        fontWeight: 'bold',
        ...sx
      }}
    >
      {children}
    </Button>
  )

  KeyButton.propTypes = {
    children: PropTypes.node,
    onClick: PropTypes.func,
    color: PropTypes.string,
    sx: PropTypes.object,
    variant: PropTypes.string,
    disabled: PropTypes.bool
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='xs'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: 'background.paper'
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>{title}</Typography>
        <IconButton onClick={onClose} size='large'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 3, pt: 1 }}>
        <Box
          sx={{
            bgcolor: 'background.default',
            p: 2,
            mb: 2,
            borderRadius: 1,
            textAlign: 'right',
            border: `1px solid ${theme.palette.divider}`,
            minHeight: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end'
          }}
        >
          <Typography variant='h3' sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
            {value}
          </Typography>
        </Box>
        <Grid container spacing={1}>
          {/* Row 1 */}
          <Grid item xs={3}><KeyButton onClick={() => handleNumberClick(7)}>7</KeyButton></Grid>
          <Grid item xs={3}><KeyButton onClick={() => handleNumberClick(8)}>8</KeyButton></Grid>
          <Grid item xs={3}><KeyButton onClick={() => handleNumberClick(9)}>9</KeyButton></Grid>
          <Grid item xs={3}>
            <KeyButton
              onClick={handleBackspace}
              sx={{
                bgcolor: 'background.color_212121',
                '&:hover': { bgcolor: 'background.buttonHover' }
              }}
            >
              <BackspaceIcon />
            </KeyButton>
          </Grid>

          {/* Row 2 */}
          <Grid item xs={3}><KeyButton onClick={() => handleNumberClick(4)}>4</KeyButton></Grid>
          <Grid item xs={3}><KeyButton onClick={() => handleNumberClick(5)}>5</KeyButton></Grid>
          <Grid item xs={3}><KeyButton onClick={() => handleNumberClick(6)}>6</KeyButton></Grid>
          <Grid item xs={3}>
            <KeyButton
              onClick={handleClear}
              sx={{
                color: 'red.main',
                bgcolor: 'background.color_212121',
                '&:hover': { bgcolor: 'background.buttonHover' }
              }}
            >
              C
            </KeyButton>
          </Grid>

          {/* Row 3 and 4 */}
          <Grid item xs={9}>
            <Grid container spacing={1}>
              <Grid item xs={4}><KeyButton onClick={() => handleNumberClick(1)}>1</KeyButton></Grid>
              <Grid item xs={4}><KeyButton onClick={() => handleNumberClick(2)}>2</KeyButton></Grid>
              <Grid item xs={4}><KeyButton onClick={() => handleNumberClick(3)}>3</KeyButton></Grid>
              <Grid item xs={4}>
                <KeyButton
                  onClick={handleDot}
                  disabled={!allowDecimals}
                  sx={{
                    opacity: allowDecimals ? 1 : 0.5,
                    pointerEvents: allowDecimals ? 'auto' : 'none'
                  }}
                >
                  .
                </KeyButton>
              </Grid>
              <Grid item xs={4}><KeyButton onClick={() => handleNumberClick(0)}>0</KeyButton></Grid>
              <Grid item xs={4}><KeyButton onClick={handleDoubleZero}>00</KeyButton></Grid>
            </Grid>
          </Grid>
          <Grid item xs={3}>
            <KeyButton
              onClick={handleAccept}
              sx={{
                height: '100%',
                fontSize: '1.25rem',
                bgcolor: 'background.green',
                color: 'white',
                '&:hover': { bgcolor: 'success.dark' }
              }}
            >
              OK
            </KeyButton>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

NumericKeypad.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAccept: PropTypes.func.isRequired,
  initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string,
  allowDecimals: PropTypes.bool
}

export default NumericKeypad
